import { useState } from 'react';
import { 
  Globe, 
  ShoppingCart, 
  Box, 
  LayoutGrid, 
  FileCode, 
  Code,
  ArrowRight
} from 'lucide-react';
import Modal from './Modal';

// Create a central platforms data store that can be accessed by both Platform cards and Footer
export const platformsData = [
  {
    id: 1,
    title: 'WordPress',
    description: 'Complete support for the world\'s most popular CMS, including themes, plugins, and core updates.',
    icon: <Globe className="h-12 w-12" />,
    logo: "https://s.w.org/style/images/about/WordPress-logotype-standard.png",
    issues: [
      'Plugin conflicts and errors',
      'Theme customization issues',
      'Performance optimization',
      'Security vulnerabilities',
      'Database errors'
    ],
    detailedDescription: 'We provide comprehensive support for WordPress websites, including everything from routine maintenance to complex troubleshooting. Our team has extensive experience with WordPress core, themes, plugins, and custom implementations.',
    detailedFeatures: [
      {
        group: 'Technical Support',
        items: [
          'WordPress core updates and maintenance',
          'Theme customization and troubleshooting',
          'Plugin conflict resolution',
          'Custom plugin development and modification',
          'Performance optimization for WordPress sites'
        ]
      },
      {
        group: 'Security & Maintenance',
        items: [
          'WordPress security hardening',
          'Malware removal and cleanup',
          'Regular backups and restoration',
          'Database optimization',
          'User management and permissions'
        ]
      },
      {
        group: 'Specialty Solutions',
        items: [
          'WooCommerce support and customization',
          'Multisite network management',
          'Custom post type implementation',
          'WordPress API integration',
          'Accessibility compliance implementation'
        ]
      }
    ],
    documentation: "https://wordpress.org/documentation/",
    learnMoreLink: "https://example.com/wordpress-support",
    modalContent: {
      title: 'WordPress Support',
      description: 'Our WordPress experts provide comprehensive support for the world\'s most popular content management system. We ensure your WordPress site runs smoothly, securely, and effectively.',
      features: [
        'Theme customization and troubleshooting',
        'Plugin management and conflict resolution',
        'Security hardening and malware removal',
        'Performance optimization',
        'Custom functionality development',
        'Database optimization',
        'WooCommerce support',
        'Update management'
      ],
      cta: {
        text: 'Get WordPress Support',
        link: '#pricing'
      }
    }
  },
  {
    id: 2,
    title: 'Shopify',
    description: 'Expert support for your Shopify store, from checkout issues to theme customizations and app integrations.',
    icon: <ShoppingCart className="h-12 w-12" />,
    logo: "https://cdn.shopify.com/shopifycloud/brochure/assets/brand-assets/shopify-logo-primary-logo-456baa801ee66a0a435671082365958316831c9960c480451dd0330bcdae304f.svg",
    issues: [
      'Checkout process errors',
      'Payment gateway issues',
      'Product display problems',
      'Theme customization',
      'App integration conflicts'
    ],
    detailedDescription: 'Our Shopify support services help store owners optimize their ecommerce operation. From theme customization to payment processing issues, we ensure your Shopify store delivers a seamless shopping experience.',
    detailedFeatures: [
      {
        group: 'Store Functionality',
        items: [
          'Checkout flow optimization',
          'Payment gateway integration and troubleshooting',
          'Product catalog management',
          'Inventory synchronization',
          'Shipping and tax configuration'
        ]
      },
      {
        group: 'Design & Customization',
        items: [
          'Shopify theme customization',
          'Responsive design implementation',
          'Custom section development',
          'Store layout optimization',
          'Brand consistency implementation'
        ]
      },
      {
        group: 'App & Integration',
        items: [
          'Third-party app integration',
          'App conflict resolution',
          'Custom app development',
          'API integration with external systems',
          'Performance optimization for app-heavy stores'
        ]
      }
    ],
    documentation: "https://help.shopify.com/",
    learnMoreLink: "https://example.com/shopify-support",
    modalContent: {
      title: 'Shopify Support',
      description: 'Our Shopify specialists help you maximize your online store\'s potential. From theme customization to app integration, we provide expert support for all your Shopify needs.',
      features: [
        'Theme customization and troubleshooting',
        'App integration and configuration',
        'Product management assistance',
        'Payment gateway setup',
        'Performance optimization',
        'Store migration services',
        'Custom functionality development',
        'SEO optimization for Shopify'
      ],
      cta: {
        text: 'Get Shopify Support',
        link: '#pricing'
      }
    }
  },
  {
    id: 3,
    title: 'Webflow',
    description: 'Professional assistance for Webflow sites, including CMS issues, interactions, and responsive design fixes.',
    icon: <LayoutGrid className="h-12 w-12" />,
    logo: "https://dhygzobemt712.cloudfront.net/Logo/Full_Logo_Blue_Black.png",
    issues: [
      'CMS collection issues',
      'Interaction animations',
      'Responsive design problems',
      'Form submission errors',
      'Layout inconsistencies'
    ],
    detailedDescription: 'Our Webflow expertise covers all aspects of this powerful platform. From complex animations to CMS collections and responsive designs, we help you leverage Webflow\'s capabilities while resolving any technical issues.',
    detailedFeatures: [
      {
        group: 'Design & Layout',
        items: [
          'Responsive design implementation',
          'Complex layout troubleshooting',
          'Webflow interactions and animations',
          'Custom code integration',
          'Design system implementation'
        ]
      },
      {
        group: 'CMS & Dynamic Content',
        items: [
          'CMS collection setup and management',
          'Dynamic content templating',
          'CMS filtering and sorting implementation',
          'Multi-reference field configuration',
          'CMS import/export assistance'
        ]
      },
      {
        group: 'Functionality & Integration',
        items: [
          'Form configuration and submission handling',
          'Third-party service integration',
          'Ecommerce functionality support',
          'Webflow API implementation',
          'Performance optimization'
        ]
      }
    ],
    documentation: "https://university.webflow.com/",
    learnMoreLink: "https://example.com/webflow-support",
    modalContent: {
      title: 'Webflow Support',
      description: 'Our Webflow experts help you harness the full power of this advanced visual website builder. From design troubleshooting to CMS setup, we provide comprehensive Webflow support.',
      features: [
        'Design system implementation',
        'Responsive design troubleshooting',
        'CMS setup and optimization',
        'Complex interactions creation',
        'Performance optimization',
        'Custom code integration',
        'Site migration to Webflow',
        'Webflow SEO optimization'
      ],
      cta: {
        text: 'Get Webflow Support',
        link: '#pricing'
      }
    }
  },
  {
    id: 4,
    title: 'Wix',
    description: 'Specialized support for Wix websites, helping with editor issues, custom code, and third-party integrations.',
    icon: <Box className="h-12 w-12" />,
    logo: "https://upload.wikimedia.org/wikipedia/commons/7/76/Wix.com_website_logo.svg",
    issues: [
      'Editor functionality problems',
      'Custom code implementation',
      'Mobile responsiveness',
      'Form and contact issues',
      'App market integrations'
    ],
    detailedDescription: 'We provide comprehensive support for Wix websites, tackling everything from editor challenges to complex code customizations. Our expertise helps you maximize the potential of your Wix site while resolving technical issues.',
    detailedFeatures: [
      {
        group: 'Site Building & Editing',
        items: [
          'Wix Editor troubleshooting',
          'Section and page layout optimization',
          'Mobile responsiveness fixes',
          'Animation and interaction assistance',
          'Design consistency implementation'
        ]
      },
      {
        group: 'Custom Development',
        items: [
          'Custom code integration with Velo',
          'JavaScript implementation',
          'API connections',
          'Custom functionality development',
          'Database and collection configuration'
        ]
      },
      {
        group: 'Apps & Integration',
        items: [
          'Wix app market integration assistance',
          'Third-party service connections',
          'Form configuration and submission handling',
          'E-commerce functionality support',
          'Booking and scheduling system setup'
        ]
      }
    ],
    documentation: "https://support.wix.com/",
    learnMoreLink: "https://example.com/wix-support",
    modalContent: {
      title: 'Wix Support',
      description: 'Our Wix experts help you make the most of this user-friendly website builder. From design customization to Velo development, we provide comprehensive Wix support services.',
      features: [
        'Template customization',
        'Wix Velo/Corvid development',
        'E-commerce setup and optimization',
        'Form creation and management',
        'SEO optimization for Wix',
        'Third-party integrations',
        'Content migration',
        'Performance optimization'
      ],
      cta: {
        text: 'Get Wix Support',
        link: '#pricing'
      }
    }
  },
  {
    id: 5,
    title: 'Squarespace',
    description: 'Complete support for Squarespace sites, including template customization, commerce issues, and CSS adjustments.',
    icon: <FileCode className="h-12 w-12" />,
    logo: "https://static.cdnlogo.com/logos/s/5/squarespace.svg",
    issues: [
      'Template customization',
      'CSS adjustments',
      'Commerce functionality',
      'Content display issues',
      'Form configuration'
    ],
    detailedDescription: 'Our Squarespace support covers all aspects of this streamlined website platform. From template customizations to e-commerce functionality, we help you resolve issues and maximize your Squarespace site\'s potential.',
    detailedFeatures: [
      {
        group: 'Design & Customization',
        items: [
          'Template customization',
          'Custom CSS implementation',
          'Design consistency across pages',
          'Mobile responsiveness optimization',
          'Typography and styling adjustments'
        ]
      },
      {
        group: 'E-commerce & Forms',
        items: [
          'Squarespace Commerce configuration',
          'Product catalog setup and management',
          'Checkout process optimization',
          'Form creation and submission handling',
          'Payment gateway integration'
        ]
      },
      {
        group: 'Content & Functionality',
        items: [
          'Content block troubleshooting',
          'Gallery and image display optimization',
          'Blog configuration and management',
          'Third-party integration',
          'SEO and metadata implementation'
        ]
      }
    ],
    documentation: "https://support.squarespace.com/",
    learnMoreLink: "https://example.com/squarespace-support",
    modalContent: {
      title: 'Squarespace Support',
      description: 'Our Squarespace specialists help you make the most of this elegant, all-in-one platform. From template customization to advanced functionality, we provide expert Squarespace support.',
      features: [
        'Template customization',
        'Custom CSS and code injection',
        'E-commerce setup and optimization',
        'Content migration and setup',
        'Third-party integration',
        'SEO optimization',
        'Performance improvements',
        'Analytics setup and interpretation'
      ],
      cta: {
        text: 'Get Squarespace Support',
        link: '#pricing'
      }
    }
  },
  {
    id: 6,
    title: 'Custom Sites',
    description: 'Expert support for custom-built websites, whether they use React, Vue, Laravel, or other frameworks.',
    icon: <Code className="h-12 w-12" />,
    logo: "https://cdn-icons-png.flaticon.com/512/2166/2166823.png", // Generic code icon
    issues: [
      'JavaScript errors',
      'API integration issues',
      'Server configuration',
      'Database optimization',
      'Front-end bugs'
    ],
    detailedDescription: 'Our custom website support covers a wide range of technologies and frameworks. Whether your site is built with React, Vue, Angular, Laravel, or other technologies, our team can help resolve issues and implement improvements.',
    detailedFeatures: [
      {
        group: 'Front-end Technologies',
        items: [
          'React troubleshooting and optimization',
          'Vue.js component fixes',
          'Angular application support',
          'JavaScript error resolution',
          'Modern CSS implementation (Sass, Less, CSS-in-JS)'
        ]
      },
      {
        group: 'Back-end Support',
        items: [
          'PHP framework support (Laravel, Symphony, CodeIgniter)',
          'Node.js application maintenance',
          'Python/Django issue resolution',
          'Ruby on Rails troubleshooting',
          'Database optimization (MySQL, PostgreSQL, MongoDB)'
        ]
      },
      {
        group: 'DevOps & Infrastructure',
        items: [
          'Server configuration and optimization',
          'Deployment pipeline fixes',
          'Performance troubleshooting',
          'Security implementation',
          'API integration and development'
        ]
      }
    ],
    documentation: "Varies by technology",
    learnMoreLink: "https://example.com/custom-website-support",
    modalContent: {
      title: 'Custom Website Support',
      description: 'Our development team provides expert support for custom-built websites using various technologies. From HTML/CSS to React applications, we can help maintain and improve your custom site.',
      features: [
        'Bug fixing and troubleshooting',
        'Feature implementation',
        'Performance optimization',
        'Security hardening',
        'Code refactoring',
        'API integration',
        'Responsive design improvements',
        'Database management'
      ],
      cta: {
        text: 'Get Custom Site Support',
        link: '#pricing'
      }
    }
  }
];

const additionalPlatformsData = [
  {
    name: 'GoDaddy',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/GoDaddy_logo.svg/1600px-GoDaddy_logo.svg.png',
  },
  {
    name: 'Hostinger',
    logo: 'https://www.freelogovectors.net/wp-content/uploads/2023/11/hostinger-logo-freelogovectors.net_-640x400.png',
  },
  {
    name: 'AWS',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
  },
  {
    name: 'Zapier',
    logo: 'https://cdn.worldvectorlogo.com/logos/zapier-2.svg',
  },
  {
    name: 'Mailchimp',
    logo: 'https://1000logos.net/wp-content/uploads/2023/04/Mailchimp-logo-1536x864.png',
  },
  {
    name: 'ClickFunnels',
    logo: 'https://www.fahimai.com/wp-content/uploads/2025/03/Best-section-img-1.png',
  },
  {
    name: 'Elementor',
    logo: 'https://elementor.com/marketing/wp-content/uploads/2021/10/Elementor-Logo-Symbol-Pink.svg',
  },
  {
    name: 'Divi',
    logo: 'https://matadigital.nz/wp-content/uploads/2024/02/divi_logo_colour.png',
  },
  {
    name: 'HubSpot',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3f/HubSpot_Logo.svg',
  },
  {
    name: 'Google Analytics',
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg',
  },
  {
    name: 'Stripe',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
  },
  {
    name: 'WooCommerce',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/WooCommerce_logo.svg/2560px-WooCommerce_logo.svg.png',
  },
];

const PlatformCard = ({ platform, openModal }) => (
  <div
    className="bg-white rounded-xl shadow-md p-6 border border-gray-100 transition-all duration-300 hover:shadow-lg hover:border-primary/20 cursor-pointer group"
    onClick={() => openModal(platform.id)}
    tabIndex="0"
    role="button"
    aria-label={`View details about ${platform.title} support`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal(platform.id);
      }
    }}
  >
    <div className="mb-6 text-primary group-hover:text-primary/80 transition-colors">
      {platform.icon}
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors flex items-center">
      {platform.title}
      <ArrowRight className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
    </h3>
    <p className="text-gray-600 mb-6">
      {platform.description}
    </p>
    <div className="mt-auto">
      <p className="font-medium text-gray-800 mb-3">Common issues we fix:</p>
      <ul className="space-y-2">
        {platform.issues.map((issue, index) => (
          <li key={index} className="text-gray-600 text-sm flex items-start">
            <span className="text-primary mr-2 flex-shrink-0">•</span>
            <span>{issue}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PlatformModal = ({ platform, isOpen, onClose }) => {
  if (!platform) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={platform.modalContent.title}
      icon={platform.icon}
      ctaText={platform.modalContent.cta.text}
      ctaLink="/register"
    >
      <div>
        {platform.logo && (
          <div className="mb-6 flex justify-center">
            <img 
              src={platform.logo} 
              alt={`${platform.title} logo`} 
              className="h-12 object-contain" 
            />
          </div>
        )}
        
        <p className="text-gray-700 text-lg mb-6">{platform.modalContent.description}</p>
        
        <div className="mt-6">
          <h4 className="font-medium text-lg mb-3">Our Services Include:</h4>
          <ul className="space-y-2">
            {platform.modalContent.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

const Platforms = ({
  activePlatformId: externalActivePlatformId,
  openPlatformModal: externalOpenPlatformModal,
  closePlatformModal: externalClosePlatformModal
}) => {
  const [localActivePlatformId, setLocalActivePlatformId] = useState(null);
  
  // Use external state if provided, otherwise use local state
  const activePlatformId = externalActivePlatformId !== undefined ? externalActivePlatformId : localActivePlatformId;
  
  const openPlatformModal = (platformId) => {
    if (externalOpenPlatformModal) {
      externalOpenPlatformModal(platformId);
    } else {
      setLocalActivePlatformId(platformId);
    }
  };
  
  const closePlatformModal = () => {
    if (externalClosePlatformModal) {
      externalClosePlatformModal();
    } else {
      setLocalActivePlatformId(null);
    }
  };
  
  const activePlatform = activePlatformId ? platformsData.find(platform => platform.id === activePlatformId) : null;

  return (
    <section id="platforms" className="bg-gray-50 py-20 md:py-24 mt-8">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 animate-fadeIn">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider mb-2 block">Platforms We Support</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Multi-Platform Support
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We provide expert support for all major website platforms, so you can get help no matter what technology you use
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {platformsData.map((platform) => (
            <PlatformCard 
              key={platform.id} 
              platform={platform}
              openModal={openPlatformModal}
            />
          ))}
        </div>

        <div className="mt-20 p-8 bg-white rounded-xl shadow-md border border-gray-100 max-w-5xl mx-auto animate-fadeIn">
          <h3 className="text-xl font-bold text-gray-900 mb-8 text-center">
            We Also Support Popular Marketing Tools & Services
          </h3>
          
          {/* Carousel for logos */}
          <div className="relative overflow-hidden">
            <div className="flex flex-wrap justify-center items-center gap-8">
              {additionalPlatformsData.map((platform, index) => (
                <div key={index} className="flex flex-col items-center p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <img 
                    src={platform.logo} 
                    alt={`${platform.name} logo`} 
                    className="h-12 w-auto object-contain mb-2"
                  />
                  <span className="text-sm text-gray-600">{platform.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Platform Modal */}
      <PlatformModal 
        platform={activePlatform}
        isOpen={!!activePlatformId}
        onClose={closePlatformModal}
      />
    </section>
  );
};

export default Platforms;